'use client'

import { LocaleIdentifier } from '@repo/markets/types'
import { NavigationQueryResult } from '@repo/sanity/types'
import { Button } from '@repo/ui/components/button'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@repo/ui/components/hover-card'
import { cn } from '@repo/ui/lib/utils'
import { FunctionComponent } from 'react'
import { useNavigation } from '../../../providers/NavigationProvider'
import LinkResolver from '../../links/LinkResolver'

interface Props {
  locale: LocaleIdentifier
  navigation: NavigationQueryResult
  links: NonNullable<NavigationQueryResult>['primaryLinks'] | NonNullable<NavigationQueryResult>['secondaryLinks'] | NonNullable<NavigationQueryResult>['supportLinks']
  className?: string
}

const NavigationLinkLoop: FunctionComponent<Props> = ({ locale, navigation, links, className }) => {
  const { shouldUseInverse } = useNavigation()
  const buttonClasses = cn(className)
  const buttonVariant = shouldUseInverse(navigation) ? 'navigationInverse' : 'navigationDefault'

  return (
    <>
      {links?.flatMap((link) => {
        if (!link?.label) {
          return []
        }

        if (link?._type === 'linkSet') {
          const subLinks = link?.links || []

          if (subLinks.length === 0) {
            return []
          }

          return (
            <li key={link._key}>
              <HoverCard>
                <HoverCardTrigger asChild={true}>
                  <Button className={buttonClasses} variant={buttonVariant}>
                    {link?.label}
                  </Button>
                </HoverCardTrigger>
                <HoverCardContent>
                  <ul className='flex flex-col gap-px'>
                    {subLinks.map((subLink) => {
                      return (
                        <li key={subLink._key}>
                          <Button className={buttonClasses} variant={buttonVariant} asChild={true}>
                            <LinkResolver link={subLink} locale={locale}>
                              {subLink?.label}
                            </LinkResolver>
                          </Button>
                        </li>
                      )
                    })}
                  </ul>
                </HoverCardContent>
              </HoverCard>
            </li>
          )
        }

        return (
          <li key={link._key}>
            <Button className={buttonClasses} variant={buttonVariant} asChild={true}>
              <LinkResolver link={link} locale={locale}>
                {link?.label}
              </LinkResolver>
            </Button>
          </li>
        )
      })}
    </>
  )
}

export default NavigationLinkLoop
